/**
 * AdminRoutes - Centralized route configuration for the Admin module.
 * Exports the nested admin route elements to be used as children of the
 * parent Route (path="/admin", element=AdminWorkspacePage) in App.js.
 * All page components are lazy-loaded to optimize bundle size.
 */

import React, { lazy } from 'react';
import { Route } from 'react-router-dom';
import { ADMIN_PATHS } from '@/shared/constants/routes';

// Lazy-loaded admin pages (layout AdminWorkspacePage is used in App.js)
const CSORegistryPage = lazy(() => import('../organizations/OrganizationsListPage'));
const InstitutionsPage = lazy(() =>
  import('../institutions/InstitutionsPage')
);
const NoticesPage = lazy(() =>
  import('../notices/NoticesPage')
);
const NoticeDetailPage = lazy(() => import('../notices/NoticeDetailPage'));
const UserOrganizationManagementPage = lazy(() =>
  import('../users/UserOrganizationManagementPage')
);
const CSOApplicationPage = lazy(() => import('../organizations/OrganizationFormPage'));
const PublicBodyApplicationPage = lazy(() => import('../institutions/InstitutionCreatePage'));
const InstitutionDetailPage = lazy(() =>
  import('../institutions/InstitutionDetailPage')
);
const ObligationFormPage = lazy(() =>
  import('@/features/admin/action-plans/ObligationFormPage')
);
const ActionPlanListPage = lazy(() =>
  import('@/features/admin/action-plans/ActionPlanListPage')
);
const ActionPlanCreatePage = lazy(() =>
  import('@/features/admin/action-plans/ActionPlanCreatePage')
);
const ActionPlanDetailPage = lazy(() =>
  import('@/features/admin/action-plans/ActionPlanDetailPage')
);
const ObligationReportFormPage = lazy(() => import('@/features/admin/action-plans/ObligationReportFormPage'));

/** Placeholder for dashboard; layout (AdminWorkspacePage) shows DashboardView for /admin. */
const DashboardPlaceholder = () => null;

const NotificationsView = lazy(() => import('./NotificationsView'));

/**
 * Nested admin route elements. Use in App.js as direct children of the admin Route:
 * <Route path="/admin" element={...}> {adminRouteElements} </Route>
 * React Router requires children of <Routes>/<Route> to be <Route> or <React.Fragment>, not a component.
 */
export const adminRouteElements = [
  <Route key="admin-index" index element={<DashboardPlaceholder />} />,
  <Route key="admin-dashboard" path={ADMIN_PATHS.DASHBOARD} element={<DashboardPlaceholder />} />,
  <Route key="admin-cso-root" path={ADMIN_PATHS.CSO.ROOT} element={<CSORegistryPage />} />,
  <Route key="admin-cso-new-ocd" path={ADMIN_PATHS.CSO.NEW_OCD} element={<CSOApplicationPage />} />,
  <Route key="admin-cso-edit" path={ADMIN_PATHS.CSO.EDIT} element={<CSOApplicationPage />} />,
  <Route key="admin-institutions-root" path={ADMIN_PATHS.INSTITUTIONS.ROOT} element={<InstitutionsPage />} />,
  <Route key="admin-institutions-new" path={ADMIN_PATHS.INSTITUTIONS.NEW} element={<PublicBodyApplicationPage />} />,
  <Route key="admin-institution-detail-alt" path={ADMIN_PATHS.INSTITUTIONS.DETAIL_ALT} element={<InstitutionDetailPage />} />,
  <Route key="admin-institution-detail" path={ADMIN_PATHS.INSTITUTIONS.DETAIL} element={<InstitutionDetailPage />} />,
  <Route key="admin-notices-root" path={ADMIN_PATHS.NOTICES.ROOT} element={<NoticesPage />} />,
  <Route key="admin-notices-new" path={ADMIN_PATHS.NOTICES.NEW} element={<NoticeDetailPage />} />,
  <Route key="admin-notices-detail" path={ADMIN_PATHS.NOTICES.DETAIL} element={<NoticeDetailPage />} />,
  <Route key="admin-notice-detail-alt-root" path={ADMIN_PATHS.NOTICES.DETAIL_ALT_ROOT} element={<NoticeDetailPage />} />,
  <Route key="admin-notice-detail-alt" path={ADMIN_PATHS.NOTICES.DETAIL_ALT} element={<NoticeDetailPage />} />,
  <Route key="admin-users" path={ADMIN_PATHS.USERS} element={<UserOrganizationManagementPage />} />,
  <Route key="admin-obligation-edit" path={ADMIN_PATHS.OBLIGATION.EDIT} element={<ObligationFormPage />} />,
  <Route key="admin-action-plan-new" path={ADMIN_PATHS.ACTION_PLAN.NEW} element={<ActionPlanCreatePage />} />,
  <Route key="admin-action-plan-list" path={ADMIN_PATHS.ACTION_PLAN.LIST} element={<ActionPlanListPage />} />,
  <Route key="admin-action-plan-detail" path={ADMIN_PATHS.ACTION_PLAN.DETAIL} element={<ActionPlanDetailPage />} />,
  <Route key="admin-action-plan-report" path={ADMIN_PATHS.ACTION_PLAN.REPORT} element={<ObligationReportFormPage />} />,
  <Route key="admin-notifications" path={ADMIN_PATHS.NOTIFICATIONS} element={<NotificationsView />} />,
];
